import { HttpClient, HttpParams } from "@angular/common/http";
import { Component } from "@angular/core";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateService } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import "./release-notes.component.scss";

export interface IReleaseNote {
    CreatedOn: Date;
    FullName: string;
    Version: string;

    PBIs: IReleaseNoteChange[];
    Enhancements: IReleaseNoteChange[];
    Bugs: IReleaseNoteChange[];
    CustomerBugs: IReleaseNoteChange[];

    Changes: IReleaseNoteChange[];
    Parents: IReleaseNoteChange[];

    IsDraft: boolean;

    Environments: string[];

    Classification: "Hotfix" | "Feature Release";
}

export interface IReleaseNoteChange {
    Id: number;
    ParentId?: number;

    Type: string;

    Title: string;
    Description: string;

    Url: string;

    CustomerRequest: boolean;
    CustomerTicketNumbers: string[];

    Parent?: IReleaseNoteChange;
    Children?: IReleaseNoteChange[];
}

@Component({
    selector: "release-notes",
    template: require("./release-notes.component.html")
})
export class ReleaseNotesComponent {
    releaseNotes: IReleaseNote[] = [];
    displayNotes: IReleaseNote[] = [];

    filterTabs: IFilterBarTab<IReleaseNote>[] = [];
    filterEvent: IAggregateFilterEvent<IReleaseNote>;

    pipeline = "";

    loading = false;

    searchTerm: string;

    constructor(
        private readonly http: HttpClient, 
        private readonly dateService: DateService,
        private readonly translationService: TranslationService
    ) {
        this.createTabs();
        this.loadData(this.pipeline, "Valkyrie Fleet");
    }

    filterBySearchText = (rn: IReleaseNote, text: string): boolean => {
        return rn.Changes.some(change => {
            const changeIdContainsText = change.Id.toString().includes(text);
            const changeTitleContainsText = change.Title.toLocaleLowerCase().includes(text);

            return changeIdContainsText || changeTitleContainsText;
        });
    }

    onFilterBarChange(event: IAggregateFilterEvent<IReleaseNote>) {
        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (this.filterEvent.trigger === "search") {
            this.searchTerm = this.filterEvent.search;
        }

        this.displayNotes = this.filterEvent.filteredData;
    }

    pipelineChanged() {
        this.loadData(this.pipeline);
    }

    showCustomerReleaseNotes() {
        window.open("https://docs.verilocation.com/release-notes/", "_blank");
    }

    // ---

    private loadData(pipeline?: string, environmentName?: string) {
        this.loading = true;

        let queryParams = new HttpParams();

        if (pipeline) {
            queryParams = queryParams.append("pipeline", pipeline);
        }

        if (environmentName) {
            queryParams = queryParams.append("environmentName", environmentName);
        }

        this.http.get<IReleaseNote[]>("/api/servicestatus/releasenotes", { params: queryParams }).subscribe(notes => {

            this.releaseNotes = notes;
            this.releaseNotes.sort((a, b) => this.dateService.sort(a.CreatedOn, b.CreatedOn));

            this.releaseNotes.forEach(rn => {
                this.prepareReleaseNote(rn);
            });

            this.loading = false;
        });
    }

    private prepareReleaseNote(rn: IReleaseNote): void {
        rn.Changes.sort((a, b) => a.Id - b.Id);

        rn.PBIs = rn.Changes.filter(c => c.Type == "Product Backlog Item" || c.Type == "Task");
        rn.Bugs = rn.Changes.filter(c => c.Type == "Bug" && !c.CustomerRequest);
        rn.CustomerBugs = rn.Changes.filter(c => c.Type == "Bug" && c.CustomerRequest);

        // Create parent trees
        rn.Enhancements = [...rn.Parents, ...rn.PBIs];
        rn.Enhancements = this.createParentHierarchy(rn, rn.Enhancements);
        rn.Enhancements = rn.Enhancements.filter(p => !p.Parent);

        rn.Classification = rn.PBIs.length < 1 ? "Hotfix" : "Feature Release";
    }

    private createParentHierarchy(rn: IReleaseNote, changes: IReleaseNoteChange[]): IReleaseNoteChange[] {
        changes.forEach(pbi => {
            if (!pbi.ParentId) {
                return;
            }

            this.findAndAssignParent(rn, pbi);
        });

        return changes.filter(c => !c.Parent);
    }

    private findAndAssignParent(rn: IReleaseNote, change: IReleaseNoteChange): boolean {
        var parent = rn.Parents.find(p => p.Id == change.ParentId);

        if (parent) {
            parent.Children = parent.Children || [];
            parent.Children.push(change);
            change.Parent = parent;

            return true;
        }

        return false;
    }

    private createTabs(): void {
        this.filterTabs = [];
        
        this.filterTabs.push({ title: this.translationService.t("ReleaseNotes.All"), predicate: rn => true });
        this.filterTabs.push({ title: this.translationService.t("ReleaseNotes.FeatureReleases"), predicate: rn => rn.Classification == "Feature Release" });
        this.filterTabs.push({ title: this.translationService.t("ReleaseNotes.Hotfixes"), predicate: rn => rn.Classification == "Hotfix" });
    }
}
